/**
 * ADR-0166 — Six-card grid item for the Benchmarks view.
 *
 * Byte-for-byte port of `BenchmarkCard` from
 * `docs/reference/Foyer_Dashboard_CURRENT.jsx` lines 7138–7452, with two
 * additions per ADR §6:
 *
 *   1. `derived_from === 'rule_count_heuristic'` renders a small muted
 *      mono caption "Heuristic · rule-count based" under the subtitle
 *      (extends the per-card honesty banner to the two coverage cards).
 *
 *   2. `card.yours === null` / `derived_from === 'unavailable'` renders
 *      a muted "Not enough data yet" state instead of a number — never
 *      a fake value (regression guard against the prototype's hardcoded
 *      4.2 / 22 / 31 constants leaking into production).
 *
 * Comparison-strip geometry is byte-preserved: P10 anchor, sky median
 * bar with a full-height `boxShadow 0 0 0 2px C.surface` ring, circular
 * yours needle with `labelOnLeft` callout when the needle sits above
 * 75% of the strip.
 */

import type { BenchmarkCard as BenchmarkCardWire } from '@sill/types';

import { Card, Tooltip } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import {
  computeAxisScale,
  fmt,
  toCardViewModel,
} from './build-benchmark-data.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";

export interface BenchmarkCardProps {
  readonly card: BenchmarkCardWire;
}

export function BenchmarkCard({ card }: BenchmarkCardProps): JSX.Element {
  const C = useC();
  const vm = toCardViewModel(card, {
    limeFg: C.limeFg,
    amberFg: C.amberFg,
    coralFg: C.coralFg,
    muted: C.muted,
  });

  // ADR §6 caption — the two coverage cards carry the "Heuristic ·
  // rule-count based" note under the subtitle so the honesty banner
  // extends per-card.
  const heuristicCaption =
    card.derived_from === 'rule_count_heuristic' ? (
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.dim,
          letterSpacing: 1,
          marginTop: 6,
        }}
      >
        Heuristic · rule-count based
      </div>
    ) : null;

  // ADR §6 unavailable state — the card still renders (the grid stays
  // six-wide) but the number is replaced by a muted "Not enough data
  // yet" state. The prototype constants 4.2 / 22 / 31 must NEVER show.
  if (card.yours === null) {
    return (
      <Card className="p-5">
        <div className="flex items-start justify-between mb-2 gap-2">
          <div className="min-w-0">
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 1.5,
                marginBottom: 4,
              }}
            >
              {card.title.toUpperCase()}
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.dim,
                lineHeight: 1.4,
              }}
            >
              {card.subtitle}
            </div>
            {heuristicCaption}
          </div>
          <Tooltip
            content={`Computed across ${card.cohort_size} anonymized merchants in this cohort.`}
          >
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.dim,
                letterSpacing: 1,
                background: C.surface2,
                padding: '2px 5px',
                border: `1px solid ${C.borderSoft}`,
                borderRadius: 2,
                flexShrink: 0,
              }}
            >
              k = {card.cohort_size}
            </span>
          </Tooltip>
        </div>

        <div
          className="mt-4 p-3"
          style={{
            background: C.surface2,
            border: `1px dashed ${C.borderSoft}`,
            borderRadius: 2,
          }}
        >
          <div
            data-testid="benchmark-card-unavailable"
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.5,
              fontStyle: 'italic',
            }}
          >
            Not enough data yet.
          </div>
        </div>
      </Card>
    );
  }

  // yours is non-null → view-model resolves percentile / verdict / summary
  const yoursValue = card.yours;
  const percentile = vm.percentile ?? 0;
  const verdict = vm.verdict ?? { color: C.muted, label: '' };
  const summary = vm.summary ?? '';

  // Axis scaling (byte-preserved from reference lines 7241–7259).
  const axis = computeAxisScale(
    yoursValue,
    card.cohort_p10,
    card.cohort_p90,
    card.unit,
  );
  const yoursPct = axis.pct(yoursValue);
  const labelOnLeft = yoursPct > 75;
  const medianPct = axis.pct(card.cohort_median);
  const medianColor = C.skyFg;

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="min-w-0">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1.5,
              marginBottom: 4,
            }}
          >
            {card.title.toUpperCase()}
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.dim,
              lineHeight: 1.4,
            }}
          >
            {card.subtitle}
          </div>
          {heuristicCaption}
        </div>
        <Tooltip
          content={`Computed across ${card.cohort_size} anonymized merchants in this cohort.`}
        >
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.dim,
              letterSpacing: 1,
              background: C.surface2,
              padding: '2px 5px',
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 2,
              flexShrink: 0,
            }}
          >
            k = {card.cohort_size}
          </span>
        </Tooltip>
      </div>

      <div className="flex items-baseline gap-3 mb-5 mt-3">
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 32,
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1,
          }}
        >
          {fmt(yoursValue, card.unit)}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: verdict.color,
            letterSpacing: 1,
            fontWeight: 600,
          }}
        >
          {verdict.label}
        </div>
      </div>

      {/* Legend chips above the strip (byte-preserved). */}
      <div
        className="flex items-center gap-4 mb-2"
        style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1 }}
      >
        <span
          className="inline-flex items-center gap-1.5"
          style={{ color: verdict.color }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              background: verdict.color,
              borderRadius: 1,
              display: 'inline-block',
            }}
          />
          YOU
        </span>
        <span
          className="inline-flex items-center gap-1.5"
          style={{ color: medianColor }}
        >
          <span
            style={{
              width: 2,
              height: 9,
              background: medianColor,
              borderRadius: 1,
              display: 'inline-block',
            }}
          />
          MEDIAN
        </span>
        <span
          className="inline-flex items-center gap-1.5"
          style={{ color: C.dim }}
        >
          <span
            style={{
              width: 16,
              height: 5,
              background: C.borderSoft,
              borderRadius: 1,
              display: 'inline-block',
            }}
          />
          COHORT (P10 — P90)
        </span>
      </div>

      {/* Comparison strip (geometry byte-preserved from reference lines 7320–7420). */}
      <div className="relative" style={{ height: 36, marginBottom: 4 }}>
        {/* Track base */}
        <div
          className="absolute"
          style={{
            left: 0,
            right: 0,
            top: 14,
            height: 10,
            background: C.surface2,
            borderRadius: 3,
          }}
        />

        {/* Cohort spread P10 → P90 */}
        <div
          className="absolute"
          style={{
            left: `${axis.pct(card.cohort_p10)}%`,
            right: `${100 - axis.pct(card.cohort_p90)}%`,
            top: 14,
            height: 10,
            background: C.borderSoft,
            borderRadius: 3,
          }}
        />

        {/* P10 anchor. */}
        <div
          className="absolute"
          style={{
            left: `${axis.pct(card.cohort_p10)}%`,
            top: 11,
            width: 1.5,
            height: 16,
          }}
        >
          <Tooltip
            content={`P10 of cohort — 10% of merchants are at or below ${fmt(card.cohort_p10, card.unit)}.`}
          >
            <div
              style={{
                width: 1.5,
                height: 16,
                background: C.dim,
                opacity: 0.7,
                borderRadius: 1,
                cursor: 'help',
              }}
            />
          </Tooltip>
        </div>

        {/* Median — full-height bar in sky color w/ surface ring. */}
        <div
          className="absolute"
          style={{
            left: `${medianPct}%`,
            top: 6,
            width: 3,
            height: 26,
            transform: 'translateX(-1.5px)',
          }}
        >
          <Tooltip
            content={`Cohort median — half of comparable merchants are below ${fmt(card.cohort_median, card.unit)}, half above.`}
          >
            <div
              style={{
                width: 3,
                height: 26,
                background: medianColor,
                borderRadius: 1,
                cursor: 'help',
                boxShadow: `0 0 0 2px ${C.surface}`,
              }}
            />
          </Tooltip>
        </div>

        {/* P90 anchor */}
        <div
          className="absolute"
          style={{
            left: `${axis.pct(card.cohort_p90)}%`,
            top: 11,
            width: 1.5,
            height: 16,
          }}
        >
          <Tooltip
            content={`P90 of cohort — 10% of merchants exceed ${fmt(card.cohort_p90, card.unit)}.`}
          >
            <div
              style={{
                width: 1.5,
                height: 16,
                background: C.dim,
                opacity: 0.7,
                borderRadius: 1,
                cursor: 'help',
              }}
            />
          </Tooltip>
        </div>

        {/* Your needle — circular marker in verdict color. */}
        <div
          className="absolute"
          style={{
            left: `${yoursPct}%`,
            top: 8,
            width: 14,
            height: 22,
            transform: 'translateX(-7px)',
          }}
        >
          <Tooltip
            content={`Your site — ${fmt(yoursValue, card.unit)} (P${percentile}).`}
          >
            <div
              style={{
                position: 'relative',
                width: 14,
                height: 22,
                cursor: 'help',
              }}
            >
              {/* Pin head */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 14,
                  height: 14,
                  background: verdict.color,
                  borderRadius: '50%',
                  border: `2px solid ${C.surface}`,
                  boxShadow: `0 0 0 1px ${verdict.color}`,
                }}
              />
              {/* Pin stem */}
              <div
                style={{
                  position: 'absolute',
                  left: 6,
                  top: 12,
                  width: 2,
                  height: 10,
                  background: verdict.color,
                }}
              />
            </div>
          </Tooltip>
        </div>

        {/* Yours value callout above the needle */}
        <div
          className="absolute"
          style={{
            left: `${yoursPct}%`,
            top: -2,
            transform: labelOnLeft
              ? 'translateX(calc(-100% - 10px))'
              : 'translateX(10px)',
            fontFamily: monoFont,
            fontSize: 9,
            color: verdict.color,
            letterSpacing: 0.5,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          YOU · {fmt(yoursValue, card.unit)}
        </div>
      </div>

      {/* Median value label under the strip */}
      <div className="relative mb-2" style={{ height: 14 }}>
        <span
          className="absolute"
          style={{
            left: `${medianPct}%`,
            transform: 'translateX(-50%)',
            fontFamily: monoFont,
            fontSize: 9,
            color: medianColor,
            letterSpacing: 0.5,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          MEDIAN {fmt(card.cohort_median, card.unit)}
        </span>
      </div>

      {/* Plain-English summary — the at-a-glance sentence. */}
      <div
        className="mt-3 p-2.5"
        style={{
          background: C.surface2,
          border: `1px solid ${C.borderSoft}`,
          borderRadius: 2,
        }}
      >
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
            lineHeight: 1.45,
            fontWeight: 500,
          }}
        >
          {summary}
        </div>
      </div>

      {/* Full interpretation paragraph */}
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
          paddingTop: 12,
          marginTop: 12,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        {card.interpretation}
      </div>
    </Card>
  );
}

